import { useState, useEffect, useCallback, useRef } from "react";
import { UserAuth } from "../../../context/AuthContext";
import { useApi } from "../../../hooks/useApi";
import config from "../../../config";

/**
 * Hook that manages WhatsApp connection (SSE for QR/status)
 * and message sending via POST /whatsapp/send.
 */
export default function useWhatsApp() {
  const { user } = UserAuth();
  const { api } = useApi();

  const [whatsappStatus, setWhatsappStatus] = useState("disconnected");
  const [qrCode, setQrCode] = useState(null);
  const [sseConnected, setSseConnected] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResults, setSendResults] = useState(null);
  const [sendError, setSendError] = useState(null);

  const eventSourceRef = useRef(null);
  const retryTimeoutRef = useRef(null);

  const connectSSE = useCallback(() => {
    if (!user?.accessToken) return;

    // Close existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const protocol = config.isDev ? "http://" : "https://";
    const port = config.isDev ? `:${config.port}` : "";
    const url = `${protocol}${config.backend}${port}/whatsapp/qr?token=${user.accessToken}`;

    const es = new EventSource(url);
    eventSourceRef.current = es;

    es.onopen = () => {
      setSseConnected(true);
    };

    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        switch (data.type) {
          case "status":
            setWhatsappStatus(data.status);
            if (data.status === "ready") setQrCode(null);
            break;
          case "qr":
            setWhatsappStatus("qr");
            setQrCode(data.qr);
            break;
          case "ready":
            setWhatsappStatus("ready");
            setQrCode(null);
            break;
          case "auth_failure":
            setWhatsappStatus("auth_failure");
            setQrCode(null);
            break;
          case "disconnected":
            setWhatsappStatus("disconnected");
            setQrCode(null);
            break;
          default:
            break;
        }
      } catch (e) {
        console.error("WhatsApp SSE parse error:", e);
      }
    };

    es.onerror = () => {
      es.close();
      setSseConnected(false);

      // Retry after 5 seconds
      retryTimeoutRef.current = setTimeout(() => {
        connectSSE();
      }, 5000);
    };
  }, [user?.accessToken]);

  const disconnectSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    setSseConnected(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnectSSE();
    };
  }, [disconnectSSE]);

  /**
   * Send WhatsApp messages to recipients.
   * @param {Array<{phone: string, name: string}>} recipients
   * @param {string} message - Message template with {{name}} placeholder
   * @returns {Promise<{results: Array}>}
   */
  const sendMessages = useCallback(
    async (recipients, message) => {
      setSending(true);
      setSendError(null);
      setSendResults(null);

      try {
        const result = await api("whatsapp/send", "POST", {
          recipients,
          message,
        });
        setSendResults(result.results);
        return result;
      } catch (err) {
        // Check if it's a 409 (WhatsApp not connected)
        if (err.status === 409) {
          setSendError("whatsapp_not_connected");
        } else {
          setSendError(err.message || "Eroare la trimiterea mesajelor.");
        }
        throw err;
      } finally {
        setSending(false);
      }
    },
    [api]
  );

  const clearSendResults = useCallback(() => {
    setSendResults(null);
    setSendError(null);
  }, []);

  /**
   * Disconnect the user's own WhatsApp session on the server.
   */
  const disconnectWhatsApp = useCallback(async () => {
    try {
      await api("whatsapp/disconnect", "POST");
      setWhatsappStatus("disconnected");
      setQrCode(null);
    } catch (err) {
      console.error("Failed to disconnect WhatsApp:", err);
    }
  }, [api]);

  return {
    // SSE / connection
    whatsappStatus,
    qrCode,
    sseConnected,
    connectSSE,
    disconnectSSE,

    // Session management
    disconnectWhatsApp,

    // Sending
    sending,
    sendResults,
    sendError,
    sendMessages,
    clearSendResults,
  };
}
